/**
 * Why a disclosure play attempt failed, as seen by the sales-tools UI. This is the UI's own-layer
 * contract — the host boundary (`onPlayDisclosure`) maps provider/SDK-specific errors to one of these
 * reasons, so the UI never parses SDK error strings or imports SDK error types.
 *
 * - `contact_not_ready` — the customer leg isn't connected yet (retryable; e.g. Play pressed during a
 *   callback before the customer answered).
 * - `in_flight` — a disclosure is already playing (retryable; concurrency, not connectivity).
 * - `conference_error` — a genuine failure to start the conference (degrade to Continue).
 */
export const DISCLOSURE_PLAY_FAILURE_REASONS = ["contact_not_ready", "in_flight", "conference_error"] as const;

export type DisclosurePlayFailureReason = (typeof DISCLOSURE_PLAY_FAILURE_REASONS)[number];

/**
 * Error the host `onPlayDisclosure` implementation throws when a play attempt fails. Carries a typed
 * {@link DisclosurePlayFailureReason} so the UI can categorize the failure (metric reason + agent
 * hint) without coupling to SDK internals.
 */
export class DisclosurePlayError extends Error {
    readonly reason: DisclosurePlayFailureReason;

    constructor(reason: DisclosurePlayFailureReason, options?: ErrorOptions) {
        super(`Disclosure play failed: ${reason}`, options);
        this.reason = reason;
        Object.setPrototypeOf(this, DisclosurePlayError.prototype);
    }
}
